import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Calendar, Heart, MessageCircle, User, Bell } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { FeatureFlag, useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useSafeTwilioConversations } from '../../contexts/twilioConversationContext.shared';
import { useNativeNotificationCount } from '../../hooks/native/useNativeNotificationCount';
import { useAppLayoutOptional } from '../../hooks/useAppLayout';
import { shouldHideBottomNav } from '../../utils/bottomNavVisibility';
import './BottomNavigationBar.css';

interface NavItem {
    label: string;
    icon: typeof Home;
    path: string;
    isActive: boolean;
    badge?: number;
}

const ACCOUNT_NAV_PATHS = ['/profile', '/profile/settings', '/animal', '/reservations', '/favorites', '/quotes', '/mes-forfaits', '/mes-abonnements', '/credits', '/mes-cadeaux', '/mes-suivis', '/compte-rendus'];

const getNativeActiveIndex = (pathname: string, chatOn: boolean): number => {
    if (pathname === '/') return 0;
    if (pathname === '/messaging') return chatOn ? 1 : -1;
    if (pathname === '/notifications') return chatOn ? 2 : 1;
    if (ACCOUNT_NAV_PATHS.includes(pathname)) return chatOn ? 3 : 2;
    return -1;
};

const BottomNavigationBar: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation('native');
    const { isAuthenticated } = useAuth();
    const { isFeatureEnabled } = useFeatureFlags();
    const chatOn = isFeatureEnabled(FeatureFlag.CHAT_FEATURE);
    const favoritesOn = isFeatureEnabled(FeatureFlag.FAVORITES);
    const { conversations } = useSafeTwilioConversations();
    const isNative = useAppLayoutOptional()?.isNative ?? false;
    const contentRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const [activePill, setActivePill] = useState<{ left: number; width: number } | null>(null);

    const unreadCount = conversations.filter(conv => conv.hasUnreadMessages).length;
    const notificationCount = useNativeNotificationCount();
    const routeAllowsBottomNav =
        !shouldHideBottomNav(location.pathname) ||
        (isNative && location.pathname === '/messaging');
    const shouldShowBottomNav = isNative
        ? routeAllowsBottomNav
        : isAuthenticated && routeAllowsBottomNav;

    const accountFamily = ACCOUNT_NAV_PATHS;

    const homeItem: NavItem = {
        label: t('bottomNav.home'),
        icon: Home,
        path: '/',
        isActive: location.pathname === '/',
    };
    const messagesItem: NavItem = {
        label: t('bottomNav.messages'),
        icon: MessageCircle,
        path: '/messaging',
        isActive: location.pathname === '/messaging',
        badge: unreadCount > 0 ? unreadCount : undefined,
    };
    const notificationsItem: NavItem = {
        label: t('bottomNav.notifications'),
        icon: Bell,
        path: '/notifications',
        isActive: location.pathname === '/notifications',
        badge: notificationCount > 0 ? notificationCount : undefined,
    };
    const accountItem: NavItem = {
        label: t('bottomNav.account'),
        icon: User,
        path: '/profile',
        isActive: accountFamily.includes(location.pathname),
    };

    const navItems: NavItem[] = isNative
        ? [
            homeItem,
            ...(chatOn ? [messagesItem] : []),
            notificationsItem,
            accountItem,
          ]
        : [
            homeItem,
            {
                label: t('bottomNav.reservations'),
                icon: Calendar,
                path: '/reservations',
                isActive: location.pathname === '/reservations',
            },
            ...(favoritesOn
              ? [
                  {
                    label: t('bottomNav.favorites'),
                    icon: Heart,
                    path: '/favorites',
                    isActive: location.pathname === '/favorites',
                  } as NavItem,
                ]
              : []),
            ...(chatOn ? [messagesItem] : []),
            {
                ...accountItem,
                isActive: ACCOUNT_NAV_PATHS.includes(location.pathname),
            },
        ];

    const syncActivePill = useCallback(() => {
        if (!isNative || !shouldShowBottomNav) return;

        const activeIndex = getNativeActiveIndex(location.pathname, chatOn);
        const activeEl = itemRefs.current[activeIndex];
        const container = contentRef.current;
        if (!activeEl || !container || activeIndex < 0) return;

        const containerRect = container.getBoundingClientRect();
        const activeRect = activeEl.getBoundingClientRect();
        const next = {
            left: activeRect.left - containerRect.left,
            width: activeRect.width,
        };

        setActivePill((prev) => {
            if (prev && prev.left === next.left && prev.width === next.width) {
                return prev;
            }
            return next;
        });
    }, [isNative, shouldShowBottomNav, location.pathname, chatOn]);

    useLayoutEffect(() => {
        if (!shouldShowBottomNav || !isNative) return undefined;

        syncActivePill();
        window.addEventListener('resize', syncActivePill);
        return () => window.removeEventListener('resize', syncActivePill);
    }, [isNative, shouldShowBottomNav, syncActivePill, location.pathname]);

    if (!shouldShowBottomNav) return null;

    const handleNavigation = (path: string, isAlreadyActive: boolean) => {
        if (
            isNative &&
            !isAuthenticated &&
            (path === '/messaging' || path === '/notifications')
        ) {
            navigate('/login', { state: { from: path } });
            return;
        }

        if (isAlreadyActive) {
            // Tap sur l'onglet déjà actif → scroll en haut (comportement natif standard)
            const scrollTarget =
                document.querySelector<HTMLElement>('.app-shell__content--native') ??
                document.querySelector<HTMLElement>('main') ??
                document.documentElement;
            scrollTarget.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            navigate(path);
        }
    };

    return (
        <nav className={`bottom-nav-bar${isNative ? ' bottom-nav-bar--native' : ''}`}>
            <div className="bottom-nav-content" ref={contentRef}>
                {isNative && activePill && (
                    <span
                        className="bottom-nav-active-pill"
                        aria-hidden="true"
                        style={{
                            width: activePill.width,
                            transform: `translateX(${activePill.left}px)`,
                        }}
                    />
                )}
                {navItems.map((item, index) => {
                    const Icon = item.icon;
                    return (
                        <button
                            key={item.path}
                            ref={(el) => {
                                itemRefs.current[index] = el;
                            }}
                            className={`bottom-nav-item ${item.isActive ? 'active' : ''}`}
                            onClick={() => handleNavigation(item.path, item.isActive)}
                            aria-label={item.label}
                            aria-current={item.isActive ? 'page' : undefined}
                        >
                            <div className="bottom-nav-icon-wrapper">
                                <Icon
                                    className="bottom-nav-icon"
                                    strokeWidth={item.isActive ? 2.5 : 2}
                                />
                                {item.badge !== undefined && (
                                    <span className="bottom-nav-badge">
                                        {item.badge > 99 ? '99+' : item.badge}
                                    </span>
                                )}
                            </div>
                            {!isNative && (
                                <span className="bottom-nav-label">{item.label}</span>
                            )}
                        </button>
                    );
                })}
            </div>
        </nav>
    );
};

export default BottomNavigationBar;
